import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { navItems, metadata } from '../data/data';
import { useActiveSection } from '../hooks/useActiveSection';
import { trackEvent } from '../lib/firebase';

const Navbar = () => {
  const activeSection = useActiveSection();
  const [menuOpen, setMenuOpen] = useState(false);
  const pendingScroll = useRef(null);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Removing the mobile menu mid-scroll cancels a smooth scroll, so when the
  // menu is open, close it first and scroll once its exit animation finishes.
  const scrollAfterMenuCloses = (scroll) => {
    if (menuOpen) {
      pendingScroll.current = scroll;
      setMenuOpen(false);
    } else {
      scroll();
    }
  };

  const handleNavClick = (e, item) => {
    e.preventDefault();
    scrollAfterMenuCloses(() => scrollToSection(item.href));

    trackEvent('section_clicked', {
      section_id: item.label,
    });
  };

  const renderLink = (item) => {
    const isActive = activeSection === item.href.substring(1);
    return (
      <a
        href={item.href}
        onClick={(e) => handleNavClick(e, item)}
        className={`text-sm font-medium tracking-wide uppercase transition-all duration-300 relative ${isActive
          ? 'text-[#8B4513]'
          : 'text-[#666666] hover:text-[#1a1a1a]'
          }`}
        style={{ fontFamily: 'var(--font-sans)' }}
      >
        {item.label}
        {isActive && (
          <span className="absolute -bottom-1 left-0 w-full h-[1px] bg-[#8B4513]" />
        )}
      </a>
    );
  };

  return (
    <motion.nav
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b border-[#d4d4d0] ${menuOpen ? 'bg-[#f8f8f6]' : 'bg-[#f8f8f6]/95'}`}
    >
      <div className="container-editorial py-4 md:py-5 flex items-center justify-between md:justify-start">
        {/* Mobile: name + menu toggle */}
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            scrollAfterMenuCloses(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
          }}
          className="md:hidden text-lg text-[#1a1a1a]"
          style={{ fontFamily: 'var(--font-serif)' }}
        >
          {metadata.name}
        </a>
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          className="md:hidden text-sm font-medium tracking-wide uppercase text-[#1a1a1a] hover:text-[#8B4513] transition-colors duration-300"
          style={{ fontFamily: 'var(--font-sans)' }}
        >
          {menuOpen ? 'Close' : 'Menu'}
        </button>

        {/* Tablet and up: inline links */}
        <ul className="hidden md:flex items-center gap-x-6 lg:gap-x-10">
          {navItems.map((item) => (
            <li key={item.label}>{renderLink(item)}</li>
          ))}
        </ul>
      </div>

      <AnimatePresence
        onExitComplete={() => {
          const scroll = pendingScroll.current;
          pendingScroll.current = null;
          if (scroll) scroll();
        }}
      >
        {menuOpen && (
          <motion.ul
            id="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="md:hidden container-editorial overflow-hidden flex flex-col"
          >
            {navItems.map((item) => (
              <li key={item.label} className="py-3 border-t border-[#d4d4d0] first:border-t-0">
                {renderLink(item)}
              </li>
            ))}
            <li className="pb-2" aria-hidden="true" />
          </motion.ul>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
